import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, CircularProgress, Tooltip, Switch } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import BusinessIcon from '@mui/icons-material/Business';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';

import companyService, { Company, CreateCompanyDto, UpdateCompanyDto } from '../services/companyService';
import EntityHistoryButton from '../components/common/EntityHistoryButton';
import ImageUpload from '../components/common/ImageUpload';
import { ConfirmDialog } from '../components/common';

import './EntityManagement.css';

const CompanyManagement: React.FC = () => {
  // Helper: resolve image source
  const resolveCompanyImageSrc = (company: Company): string => {
    const anyC = company as any;
    if (anyC?.imageUrl && typeof anyC.imageUrl === 'string' && anyC.imageUrl.startsWith('http')) {
      return anyC.imageUrl;
    }
    const possible = anyC?.imageBase64;
    if (typeof possible === 'string' && possible.startsWith('data:image')) return possible;
    return companyService.getImageUrl(company.id);
  };

  // State
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);

  // Form
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formIsActive, setFormIsActive] = useState<boolean>(true);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Fetch companies
  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const response = await companyService.getCompanies(
        page - 1,
        rowsPerPage,
        searchTerm,
        showInactive ? undefined : true
      );
      setCompanies(response.items || []);
      setTotalCount(response.meta?.totalItems || 0);
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, showInactive]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Handlers
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleOpenDialog = (company: Company | null) => {
    setCurrentCompany(company);
    if (company) {
      setFormName(company.name);
      setFormDescription(company.description || '');
      setFormIsActive(Boolean(company.isActive));
    } else {
      setFormName('');
      setFormDescription('');
      setFormIsActive(true);
      setImageFile(null);
    }
    setFormError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentCompany(null);
  };

  const handleSaveForm = async () => {
    if (!formName) {
      setFormError('لطفا نام شرکت را وارد کنید');
      return;
    }
    setSubmitLoading(true);
    try {
      if (currentCompany) {
        const companyData: UpdateCompanyDto = {
          name: formName,
          description: formDescription,
          isActive: formIsActive
        };
        await companyService.updateCompany(currentCompany.id, companyData, imageFile || undefined);
      } else {
        const companyData: CreateCompanyDto = {
          name: formName,
          description: formDescription,
          isActive: formIsActive
        };
        await companyService.createCompany(companyData, imageFile || undefined);
      }
      handleCloseDialog();
      fetchCompanies();
    } catch (error: any) {
      console.error('Error saving company:', error);
      setFormError(error.response?.data?.message || 'خطا در ذخیره شرکت');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteDialogOpen = (company: Company) => {
    setCompanyToDelete(company);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setCompanyToDelete(null);
  };

  const handleDeleteCompany = async () => {
    if (!companyToDelete) return;
    setSubmitLoading(true);
    try {
      await companyService.deleteCompany(companyToDelete.id);
      fetchCompanies();
    } catch (error) {
      console.error('Error deleting company:', error);
    } finally {
      setSubmitLoading(false);
      handleDeleteDialogClose();
    }
  };

  const handleToggleStatus = async (company: Company) => {
    try {
      await companyService.toggleCompanyStatus(company.id);
      fetchCompanies();
    } catch (error) {
      console.error('Error toggling company status:', error);
    }
  };

  const handleRestoreCompany = () => {
    fetchCompanies();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSearchInput('');
    setShowInactive(false);
    setPage(1);
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const startItem = (page - 1) * rowsPerPage + 1;
  const endItem = Math.min(page * rowsPerPage, totalCount);
  const hasActiveFilters = searchTerm || showInactive;

  const activeCompanies = companies.filter(c => c.isActive).length;
  const inactiveCompanies = companies.filter(c => !c.isActive).length;

  return (
    <div className="entity-page">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">مدیریت شرکت‌های سازنده</h1>
          <p className="entity-subtitle">مدیریت برندها و شرکت‌های تولیدکننده</p>
        </div>
        <div className="header-actions">
          <button className="header-btn refresh" onClick={fetchCompanies} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
          <button className="header-btn primary" onClick={() => handleOpenDialog(null)}>
            <AddIcon fontSize="small" />
            افزودن شرکت
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><BusinessIcon /></div>
          <div>
            <p className="entity-stat-label">کل شرکت‌ها</p>
            <p className="entity-stat-value">{totalCount}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><CheckCircleIcon /></div>
          <div>
            <p className="entity-stat-label">فعال</p>
            <p className="entity-stat-value">{activeCompanies}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon red"><BlockIcon /></div>
          <div>
            <p className="entity-stat-label">غیرفعال</p>
            <p className="entity-stat-value">{inactiveCompanies}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجوی شرکت..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">نمایش</label>
            <label className="filter-toggle" style={{ height: 40 }}>
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => { setShowInactive(e.target.checked); setPage(1); }}
              />
              نمایش غیرفعال‌ها
            </label>
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {searchTerm && (
              <span className="filter-tag">
                جستجو: {searchTerm}
                <button onClick={() => { setSearchTerm(''); setSearchInput(''); }}>×</button>
              </span>
            )}
            {showInactive && (
              <span className="filter-tag">
                شامل غیرفعال‌ها
                <button onClick={() => setShowInactive(false)}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={clearFilters}>پاک کردن همه</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading ? (
          <div className="entity-loading">
            <CircularProgress size={32} />
          </div>
        ) : companies.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><BusinessIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">هیچ شرکتی یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>شرکت</th>
                  <th>توضیحات</th>
                  <th>وضعیت</th>
                  <th style={{ width: 140 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr key={company.id}>
                    <td>
                      <div className="entity-info-cell">
                        <div className="entity-image">
                          {company.id ? (
                            <img
                              src={resolveCompanyImageSrc(company)}
                              alt={company.name}
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          ) : null}
                          <BusinessIcon className="hidden" />
                        </div>
                        <div className="entity-info">
                          <span className="entity-name">{company.name}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="entity-desc">{company.description || '-'}</span>
                    </td>
                    <td>
                      <span className={`tag tag-sm ${company.isActive ? 'tag-active' : 'tag-inactive'}`}>
                        {company.isActive ? 'فعال' : 'غیرفعال'}
                      </span>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <Tooltip title="ویرایش" arrow>
                          <button className="action-btn edit" onClick={() => handleOpenDialog(company)}>
                            <EditIcon />
                          </button>
                        </Tooltip>
                        <Tooltip title="حذف" arrow>
                          <button className="action-btn delete" onClick={() => handleDeleteDialogOpen(company)}>
                            <DeleteIcon />
                          </button>
                        </Tooltip>
                        <Tooltip title={company.isActive ? 'غیرفعال کردن' : 'فعال کردن'} arrow>
                          <button
                            className={`action-btn toggle ${company.isActive ? 'active' : ''}`}
                            onClick={() => handleToggleStatus(company)}
                          >
                            {company.isActive ? <ToggleOnIcon /> : <ToggleOffIcon />}
                          </button>
                        </Tooltip>
                        <EntityHistoryButton
                          entityType="Company"
                          entityId={company.id}
                          size="small"
                          variant="text"
                          onVersionRestored={handleRestoreCompany}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {companies.map((company) => (
                <div key={company.id} className={`entity-card ${!company.isActive ? 'inactive' : ''}`}>
                  <div className="entity-card-header">
                    <div className="entity-card-image">
                      {company.id ? (
                        <img
                          src={resolveCompanyImageSrc(company)}
                          alt={company.name}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : <BusinessIcon />}
                    </div>
                    <div className="entity-card-info">
                      <div className="entity-card-name">{company.name}</div>
                      {company.description && (
                        <div className="entity-card-desc">{company.description}</div>
                      )}
                    </div>
                    <div className="entity-card-status">
                      <span className={`tag tag-sm ${company.isActive ? 'tag-active' : 'tag-inactive'}`}>
                        {company.isActive ? 'فعال' : 'غیرفعال'}
                      </span>
                    </div>
                  </div>
                  <div className="entity-card-actions">
                    <button className="entity-card-btn primary" onClick={() => handleOpenDialog(company)}>
                      <EditIcon fontSize="small" /> ویرایش
                    </button>
                    <button className="entity-card-btn" onClick={() => handleToggleStatus(company)}>
                      {company.isActive ? <ToggleOffIcon fontSize="small" /> : <ToggleOnIcon fontSize="small" />}
                      {company.isActive ? 'غیرفعال' : 'فعال'}
                    </button>
                    <button className="entity-card-btn danger" onClick={() => handleDeleteDialogOpen(company)}>
                      <DeleteIcon fontSize="small" /> حذف
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {startItem} تا {endItem} از {totalCount}
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-select"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <button
                  className="pagination-btn"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                >
                  <ChevronRightIcon fontSize="small" />
                </button>
                <span style={{ fontSize: 12 }}>{page} / {totalPages || 1}</span>
                <button
                  className="pagination-btn"
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                >
                  <ChevronLeftIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth className="company-edit-dialog">
        <div className="edit-dialog-title">
          <span>{currentCompany ? 'ویرایش شرکت' : 'افزودن شرکت جدید'}</span>
          <button className="details-close" onClick={handleCloseDialog}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {formError && <div className="dialog-error">{formError}</div>}
          <div className="edit-form">
            {/* Basic Info */}
            <div className="edit-card">
              <div className="edit-card-header">اطلاعات اصلی</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>نام شرکت <span style={{ color: '#dc2626' }}>*</span></label>
                    <input
                      type="text"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      placeholder="مثلا: لورآل"
                    />
                  </div>
                  <div className="edit-field full">
                    <label>توضیحات</label>
                    <textarea
                      value={formDescription}
                      onChange={(e) => setFormDescription(e.target.value)}
                      placeholder="توضیحات شرکت (اختیاری)"
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Settings */}
            <div className="edit-card">
              <div className="edit-card-header">تنظیمات</div>
              <div className="edit-card-body">
                <div className="edit-switches">
                  <label className="edit-switch-item">
                    <div>
                      <div className="edit-switch-label">وضعیت فعال</div>
                      <div className="edit-switch-desc">شرکت در سایت نمایش داده شود</div>
                    </div>
                    <Switch size="small" checked={formIsActive} onChange={(e) => setFormIsActive(e.target.checked)} />
                  </label>
                </div>
              </div>
            </div>

            {/* Logo */}
            <div className="edit-card">
              <div className="edit-card-header">لوگوی شرکت</div>
              <div className="edit-card-body">
                <ImageUpload
                  onChange={setImageFile}
                  currentImageUrl={currentCompany ? resolveCompanyImageSrc(currentCompany) : undefined}
                  label="انتخاب تصویر"
                  height={120}
                />
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={handleCloseDialog}>انصراف</button>
          <button className="edit-btn submit" onClick={handleSaveForm} disabled={submitLoading}>
            {submitLoading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            {currentCompany ? 'ذخیره تغییرات' : 'افزودن شرکت'}
          </button>
        </div>
      </Dialog>

      {/* Delete Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="حذف شرکت"
        message={
          <span>
            آیا از حذف شرکت <strong>«{companyToDelete?.name}»</strong> اطمینان دارید؟
            <br />حذف شرکت روی محصولات مرتبط تأثیر می‌گذارد.
          </span>
        }
        cancelText="انصراف"
        confirmText="حذف شرکت"
        confirmColor="error"
        loading={submitLoading}
        onCancel={handleDeleteDialogClose}
        onConfirm={handleDeleteCompany}
      />
    </div>
  );
};

export default CompanyManagement;
